import { useEffect, useRef, useState, useCallback } from 'react';
import type { LogEntry } from '@/types';

const MAX_LOG_ENTRIES = 500;

/** WebSocket subclass-style flag for "we're closing this on purpose,
 *  don't reconnect when onclose fires". Carried on the WS instance
 *  itself rather than a shared ref so an in-flight close on the OLD
 *  socket doesn't accidentally schedule a reconnect against the NEW
 *  one. (That bug fired the welcome message every 3s — every cleanup
 *  reset a shared `intentionalClose` ref to false before the old
 *  socket's async `onclose` had a chance to read it.) */
type TaggedWebSocket = WebSocket & { __intentional?: boolean };

export function useWebSocket() {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<TaggedWebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const connect = useCallback(() => {
    const token = localStorage.getItem('dbgo_token');
    if (!token) return;

    // Cancel any pending reconnect — we're (re)connecting now.
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }

    // Close any existing connection first. Marking it intentional on
    // the instance itself means the old socket's onclose won't fight
    // with the new one.
    if (wsRef.current) {
      wsRef.current.__intentional = true;
      wsRef.current.close();
      wsRef.current = null;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const url = `${protocol}//${host}/ws/logs?token=${encodeURIComponent(token)}`;

    const ws = new WebSocket(url) as TaggedWebSocket;
    wsRef.current = ws;

    ws.onopen = () => setConnected(true);

    ws.onmessage = (event) => {
      try {
        const entry: LogEntry = JSON.parse(event.data);
        setLogs((prev) => {
          const next = [...prev, entry];
          return next.length > MAX_LOG_ENTRIES ? next.slice(-MAX_LOG_ENTRIES) : next;
        });
      } catch { /* ignore malformed messages */ }
    };

    ws.onclose = () => {
      setConnected(false);
      // Reconnect only when this specific socket closed unexpectedly,
      // not when we deliberately tore it down.
      if (!ws.__intentional) {
        reconnectTimer.current = setTimeout(connect, 3000);
      }
    };

    ws.onerror = () => ws.close();
  }, []);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
      if (wsRef.current) {
        wsRef.current.__intentional = true;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  const clearLogs = useCallback(() => setLogs([]), []);

  return { logs, connected, clearLogs };
}
